import { useMemo } from 'react';

import { useChainId } from '@occc-app/Chain/hooks';
import { useGetLocationsQuery } from '@occc-app/dataAccessLayer/location/queries';
import { useGetOfferByIds } from '@occc-app/dataAccessLayer/offer/queries';
import { useGetProductGroupsByIds } from '@occc-app/dataAccessLayer/productGroup/queries';
import {
  AudienceEventConditionFragment,
  LocationFragment,
  LocationGroupFragment,
  ProductGroupFragment,
} from '@occc/graphql';
import { QualifiedEventAttribute } from '@occc-app/routes/Audience/types';
import { useGetLocationGroupsByUuids } from '@occc-app/dataAccessLayer/locationGroup/queries';

const collectEventData = (
  qualifiedEventsConditions: AudienceEventConditionFragment[] | undefined | null,
) => {
  const loyaltyEventUuids = [
    ...new Set(
      (qualifiedEventsConditions ?? [])
        .map((eventCondition) => eventCondition.loyaltyEventUuid)
        .filter((uuid): uuid is string => Boolean(uuid)),
    ),
  ];

  const locationUuids = [
    ...new Set(
      (qualifiedEventsConditions ?? []).flatMap(
        (eventCondition) => eventCondition.purchaseCondition?.purchasedLocationUuids || [],
      ),
    ),
  ];

  const productGroupUuids = [
    ...new Set(
      (qualifiedEventsConditions ?? []).flatMap(
        (eventCondition) => eventCondition.purchaseCondition?.productGroupUuids || [],
      ),
    ),
  ];

  const locationGroupUuids = [
    ...new Set(
      (qualifiedEventsConditions ?? []).flatMap(
        (eventCondition) => eventCondition.purchaseCondition?.purchasedLocationGroupUuids || [],
      ),
    ),
  ];

  return { locationUuids, productGroupUuids, loyaltyEventUuids, locationGroupUuids };
};

export const useGetQualifiedEventsTargetingAttribute = (
  qualifiedEventsConditions: AudienceEventConditionFragment[] | undefined | null,
): QualifiedEventAttribute[] => {
  const chainId = useChainId();

  const { locationUuids, locationGroupUuids, productGroupUuids, loyaltyEventUuids } = useMemo(
    () => collectEventData(qualifiedEventsConditions),
    [qualifiedEventsConditions],
  );

  const { data: eventPurchasedLocations } = useGetLocationsQuery({
    skip: locationUuids.length === 0,
    variables: {
      filter: {
        uuidContains: locationUuids,
      },
    },
  });

  const { data: eventPurchasedLocationGroups } = useGetLocationGroupsByUuids(locationGroupUuids);

  const { data: eventProductGroups } = useGetProductGroupsByIds(productGroupUuids);

  const { data: loyaltyEvents } = useGetOfferByIds(loyaltyEventUuids, chainId, {
    skip: loyaltyEventUuids.length === 0,
  });

  const getPurchaseCondition = (eventCondition: AudienceEventConditionFragment) => {
    const purchaseCondition = eventCondition.purchaseCondition;

    if (!purchaseCondition) {
      return undefined;
    }

    const locations = purchaseCondition.purchasedLocationUuids
      ?.map((locationUuid) =>
        eventPurchasedLocations?.find((location) => location.uuid === locationUuid),
      )
      .filter((location): location is LocationFragment => Boolean(location));

    const locationGroups = purchaseCondition.purchasedLocationGroupUuids
      ?.map((locationGroupUuid) =>
        eventPurchasedLocationGroups?.find(
          (locationGroup) => locationGroup.uuid === locationGroupUuid,
        ),
      )
      .filter((group): group is LocationGroupFragment => Boolean(group));

    const productGroups = purchaseCondition.productGroupUuids
      ?.map((productGroupUuid) =>
        eventProductGroups?.find((group) => group.id === productGroupUuid),
      )
      .filter((group): group is ProductGroupFragment => Boolean(group));

    return {
      ...purchaseCondition,
      locations: locations ?? [],
      locationGroups: locationGroups ?? [],
      productGroups: productGroups ?? [],
    };
  };

  if (!qualifiedEventsConditions) {
    return [];
  }

  return qualifiedEventsConditions.map((eventCondition) => {
    return {
      ...eventCondition,
      loyaltyEvent: loyaltyEvents?.find(
        (loyaltyEvent) => loyaltyEvent.id === eventCondition.loyaltyEventUuid,
      ),
      purchaseCondition: getPurchaseCondition(eventCondition),
    };
  });
};
